/**
 * T-Bill present-value (yield) pricing, ACT/364 convention.
 *
 * Formula (matches PM Excel):
 *   cashPrice = FV / (1 + (r × days / 364))
 * where:
 *   FV = face value at maturity (amount received at maturity)
 *   r = annual discount rate as decimal (e.g. 0.10 for 10%)
 *   days = actual calendar days from value date to maturity date
 *
 * Price per 100 (quote-style):
 *   pricePer100 = 100 / (1 + (r × days / 364))
 */

const MS_PER_DAY = 86400000;

/** Day-count basis for T-Bill discounting (Sri Lanka T-bill convention). */
export const TBILL_YEAR_BASIS = 364;

/**
 * Parse YYYY-MM-DD to UTC midnight for stable day differences.
 * @param {string} ymd
 * @returns {number|null} timestamp ms
 */
function parseYMDUtc(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const t = Date.UTC(y, mo, d);
  return Number.isNaN(t) ? null : t;
}

/**
 * Whole calendar days from value date (inclusive start) to maturity (exclusive of same-day edge cases).
 * Typically: days from value date to maturity = difference in calendar dates.
 * @param {string} valueDateYmd - YYYY-MM-DD
 * @param {string} maturityDateYmd - YYYY-MM-DD
 * @returns {number|null} positive integer days, or null if invalid
 */
export function daysBetweenYMD(valueDateYmd, maturityDateYmd) {
  const t0 = parseYMDUtc(valueDateYmd);
  const t1 = parseYMDUtc(maturityDateYmd);
  if (t0 == null || t1 == null) return null;
  const diff = Math.round((t1 - t0) / MS_PER_DAY);
  return diff;
}

/**
 * Present-value (yield) factor: 1 / (1 + r * days / 364)
 * @param {number} annualRateDecimal - e.g. 0.10 for 10%
 * @param {number} days - positive whole days
 * @param {number} [yearBasis=TBILL_YEAR_BASIS]
 */
export function discountFactor(annualRateDecimal, days, yearBasis = TBILL_YEAR_BASIS) {
  if (!Number.isFinite(annualRateDecimal) || !Number.isFinite(days) || days <= 0 || yearBasis <= 0) {
    return null;
  }
  const denominator = 1 + (annualRateDecimal * days) / yearBasis;
  if (denominator <= 0) return null;
  const f = 1 / denominator;
  return f;
}

/**
 * Round cash settlement UP to the nearest ones place (no decimal currency).
 * e.g. 3005737.5000 → 3005738
 */
export function ceilSettlementToOnes(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return n;
  // Neutralize FP noise at 4dp (price-derived cash precision), then ceil to ones.
  const at4 = Math.round(n * 10000) / 10000;
  return Math.ceil(at4 - 1e-9);
}

/**
 * Cash price (amount paid today) for full notional FV at maturity.
 * @param {{ faceValue: number, annualRateDecimal: number, days: number }} params
 * @returns {number|null}
 */
export function computeTbillCashPrice({ faceValue, annualRateDecimal, days }) {
  const fv = Number(faceValue);
  if (!Number.isFinite(fv) || fv <= 0) return null;
  const f = discountFactor(annualRateDecimal, days);
  if (f == null || !Number.isFinite(f)) return null;
  return ceilSettlementToOnes(fv * f);
}

/**
 * Price per 100 par (quote-style).
 */
export function computeTbillPricePer100({ annualRateDecimal, days }) {
  const f = discountFactor(annualRateDecimal, days);
  if (f == null) return null;
  return 100 * f;
}

/**
 * Convert UI annual discount rate from percent to decimal (10 -> 0.10).
 */
export function annualRatePercentToDecimal(percent) {
  const p = Number(percent);
  if (!Number.isFinite(p)) return null;
  return p / 100;
}

/**
 * Full pricing result for UI + server.
 */
export function computeTbillPricing({
  valueDateYmd,
  maturityDateYmd,
  faceValue,
  annualDiscountRatePercent
}) {
  const days = daysBetweenYMD(valueDateYmd, maturityDateYmd);
  const r = annualRatePercentToDecimal(annualDiscountRatePercent);
  const fv = Number(faceValue);

  if (days == null || r == null || !Number.isFinite(fv) || fv <= 0) {
    return {
      ok: false,
      days: days ?? null,
      daysToMaturity: null,
      cashPrice: null,
      pricePer100: null,
      discountEarned: null,
      discountFactor: null
    };
  }

  if (days <= 0) {
    return {
      ok: false,
      days,
      daysToMaturity: days,
      cashPrice: null,
      pricePer100: null,
      discountEarned: null,
      discountFactor: null,
      error: 'Value date must be before maturity date'
    };
  }

  const df = discountFactor(r, days);
  if (df == null || df < 0) {
    return {
      ok: false,
      days,
      daysToMaturity: days,
      cashPrice: null,
      pricePer100: null,
      discountEarned: null,
      discountFactor: df,
      error: 'Invalid discount rate or days'
    };
  }

  // Quote-style price per 100, rounded to 4 decimals (market convention).
  // Settlement is derived from that price, then rounded UP to the nearest ones
  // place (no decimal settlement amounts for T-Bill cash).
  const pricePer100 = Math.round(100 * df * 10000) / 10000;
  const cashPriceRaw = fv * (pricePer100 / 100);
  const cashPrice = ceilSettlementToOnes(cashPriceRaw);
  const discountEarned = fv - cashPrice;

  return {
    ok: true,
    days,
    daysToMaturity: days,
    cashPrice,
    pricePer100,
    discountEarned,
    discountFactor: df,
    annualRateDecimal: r
  };
}
